import { Input as HeroInput } from '@heroui/react';
import React from 'react';

export interface FormInputProps {
  /** Input field ID */
  id: string;
  /** Input field name */
  name?: string;
  /** Input field label */
  label: string;
  /** Content rendered next to the label, e.g. a copy button */
  labelEndContent?: React.ReactNode;
  /** Input field value */
  value?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Input type */
  type?: 'text' | 'email' | 'tel' | 'password' | 'number' | 'url';
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is invalid */
  isInvalid?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Helper text to display */
  helperText?: string;
  /** Where to render helper text (above or below the input) */
  helperPlacement?: 'above' | 'below';
  /** Content rendered inside the input at the start (e.g. a currency symbol) */
  startContent?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

export const Input: React.FC<FormInputProps> = ({
  id,
  name,
  label,
  labelEndContent,
  value,
  placeholder,
  type = 'text',
  required = false,
  disabled = false,
  isInvalid = false,
  errorMessage,
  onChange,
  helperText,
  helperPlacement = 'below',
  startContent,
  className = '',
}) => {
  const inputClassNames = {
    input: 'text-foreground bg-transparent px-3 py-2',
    inputWrapper:
      'bg-default-100 border-default-300 hover:border-default-400 focus-within:border-primary shadow-sm hover:shadow-md transition-all duration-200',
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-danger ml-1">*</span>}
        </label>
        {labelEndContent}
      </div>
      {helperText && helperPlacement === 'above' && (
        <div className="text-sm text-default-500">{helperText}</div>
      )}
      <HeroInput
        id={id}
        name={name}
        value={value}
        placeholder={placeholder}
        type={type}
        variant="bordered"
        isDisabled={disabled}
        isInvalid={isInvalid}
        isRequired={required}
        errorMessage={errorMessage}
        onValueChange={onChange}
        startContent={startContent}
        classNames={inputClassNames}
      />
      {helperText && helperPlacement === 'below' && (
        <div className="text-sm text-default-500">{helperText}</div>
      )}
    </div>
  );
};
